/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable style/multiline-ternary */
'use client';

import ConfirmationDialog from '@/components/ConfirmationDialog';
import { db } from '@/firebase';

import { timeAgo } from '@/utils/timeAgo';

import { getLocale } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@radix-ui/react-avatar';
import { onValue, ref, remove } from 'firebase/database';
import { Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GolfAvatar } from '../ui/GolfAvatar';
import DiscussionsHeader from './DiscussionsHeader';
import PlayerSearch from '../PlayerSearch';
import GolfIcon from '../GolfIcon';

type ChatListProps = {
  userId: string | undefined;
  activeChat: string | null;
  setActiveChat: React.Dispatch<React.SetStateAction<string | null>>;
  openNewChat: () => void;
};

type Chat = {
  id: string;
  name?: string;
  type: 'group' | 'direct';
  participants: string[];
  lastMessageTime: string;
  lastMessage?: string;
  imageUrl?: string;
  readBy?: {
    [userId: string]: string;
  };
};

type User = {
  name: string;
  avatar: string;
  online: boolean;
};

export default function ChatList({ userId, activeChat, setActiveChat, openNewChat }: ChatListProps) {
  const [chats, setChats] = useState<Chat[]>([]);
  const [users, setUsers] = useState<{ [key: string]: User }>({});
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState<string | null>(null);
  const { t } = useTranslation();

  const formatMessageTime = (timestamp: string) => {
    const messageDate = new Date(timestamp);
    const now = new Date();

    // Check if the message is from today
    if (messageDate.toDateString() === now.toDateString()) {
      return messageDate.toLocaleTimeString(getLocale(), {
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    // For older messages, use timeAgo
    return timeAgo(messageDate, t);
  };

  // Fetch users from database
  useEffect(() => {
    const usersRef = ref(db, 'users');
    onValue(usersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setUsers(data);
      }
    });
  }, []);

  // Fetch chats from database
  useEffect(() => {
    const chatsRef = ref(db, 'chats');
    onValue(chatsRef, (snapshot) => {
      const data = snapshot.val();
      const chatList: Chat[] = data
        ? Object.entries(data)
          .map(([id, details]) => ({
            ...details as Chat,
            id,
          }))
          .filter(chat => chat.participants.includes(userId!))
          .sort((a, b) => {
            const timeA = a.lastMessageTime ? new Date(a.lastMessageTime).getTime() : 0;
            const timeB = b.lastMessageTime ? new Date(b.lastMessageTime).getTime() : 0;
            return timeB - timeA; // Sort in descending order (most recent first)
          })
        : [];
      setChats(chatList);
    });
  }, [userId]);

  const deleteChat = (chatId: string) => {
    const chatRef = ref(db, `chats/${chatId}`);
    remove(chatRef);
  };

  const handleDeleteChat = () => {
    if (chatToDelete) {
      deleteChat(chatToDelete);
      setChatToDelete(null);
      setIsDeleteDialogOpen(false);
      setActiveChat(null);
    }
  };

  const isUnread = (chat: Chat) => {
    if (!userId || !chat.lastMessage) {
      return false;
    }

    return chat.participants?.includes(userId)
      && chat.lastMessage
      && (!chat.readBy?.[userId]
        || new Date(chat.lastMessageTime) > new Date(chat.readBy[userId]));
  };

  return (
    <div className="flex size-full flex-col border-r bg-card lg:w-1/3 lg:max-w-[420px]">
      {/* Header */}
      <DiscussionsHeader openNewChat={openNewChat} />

      {/* Chat List */}
      <div className="flex-1 overflow-y-auto">
        {chats.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-4 md:hidden">
            <div className="flex h-full gap-2 flex-col items-center justify-center">
              <GolfIcon name="message-multiple" className="size-32 brightness-50¨" />
              <p className="text-lg font-medium text-foreground">{t('no_messages_yet')}</p>
              <p className="text-sm text-muted-foreground">{t('start_conversation')}</p>
              <button
                onClick={openNewChat}
                className="mt-4 rounded-full bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
              >
                {t('new_message')}
              </button>
              <PlayerSearch />
            </div>
          </div>
        ) : (
          chats.map(chat => (
            /* eslint-disable-next-line jsx-a11y/no-static-element-interactions */
            <div
              key={chat.id}
              onTouchStart={() => setActiveChat(chat.id)}
              onClick={() => setActiveChat(chat.id)}
              className={`group flex cursor-pointer items-center justify-between gap-4 p-4 ${activeChat === chat.id
                ? 'bg-muted' // Highlight selected chat
                : 'hover:bg-neutral-200 dark:hover:bg-neutral-800' // Hover effect for unselected chats
                }`}
            >
              {/* Left Section: Avatar and Info */}
              <div className="flex w-full items-center gap-2">
                <div>
                  {chat.type === 'direct' ? (
                    <>
                      <GolfAvatar size={48} imageUrl={(() => {
                        const otherUserId = chat.participants.find(id => id !== userId);
                        return otherUserId && users[otherUserId]?.avatar && users[otherUserId].avatar !== '' ? users[otherUserId].avatar : '/assets/images/fallback.jpg';
                      })()} />
                    </>
                  ) : (
                    <Avatar className="block size-12 content-center border-input border rounded-full bg-primary text-center font-bold">
                      <AvatarImage className="size-12 rounded-full bg-secondary object-cover" height={48} width={48} src={chat.imageUrl && chat.imageUrl !== '' ? chat.imageUrl : '/assets/images/fallback.jpg'} alt="avatar picture" />
                      <AvatarFallback>GRP</AvatarFallback>
                    </Avatar>
                  )}
                </div>
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex justify-between">
                    <h3 className={`${isUnread(chat) ? 'font-bold' : ''}`}>
                      {chat.type === 'direct' ? (() => {
                        const otherUserId = chat.participants.find(id => id !== userId);
                        return otherUserId ? users[otherUserId]?.name : 'Unknown';
                      })() : chat.name ? chat.name : t('group_chat')}
                    </h3>
                    <p className={`shrink-0 text-xs ${isUnread(chat) ? 'text-foreground' : 'text-gray-400'} mt-1 group-hover:hidden`}>
                      {chat.lastMessageTime ? formatMessageTime(chat.lastMessageTime) : ''}
                    </p>
                  </div>
                  <p className={`w-full truncate text-sm ${isUnread(chat) ? 'text-foreground' : 'text-gray-400'}`}>
                    {chat.lastMessage || t('no_messages_yet')}
                  </p>
                </div>
              </div>

              {/* Right Section: Delete Button */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent triggering the setActiveChat when clicking delete
                  setChatToDelete(chat.id);
                  setIsDeleteDialogOpen(true);
                }}
                className="hidden rounded-full p-2 hover:bg-red-600 group-hover:block"
                title="Delete chat"
              >
                <Trash2 className="size-5 text-red-400" />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        level="destructive"
        title={`${t('delete_chat')}?`}
        cancelText={t('cancel')}
        confirmText={t('delete')}
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteChat}
      />
    </div>
  );
}
